import {
  outerHTML,
  closest,
  removeNode,
  removeClass,
  addClass,
  hasClass,
  createEvent,
} from '@utils/domUtils';

import { updateNonce } from '@utils/request';

/* global Dropzone, APP */

let fileUploadCount = 0;

export default class FileUpload {
  static selectorString() {
    return '.FileUpload';
  }

  getUploadedFiles() {
    return [...this.node.querySelectorAll('.dropzone-item')]
      .map(
        element =>
          element.file || {
            dataURL: element.getAttribute('data-preview'),
            src: element.getAttribute('data-src'),
            name: element.getAttribute('data-filename'),
          },
      )
      .filter(file => !!(file && file.dataURL && file.dataURL.length > 0));
  }

  removeUploadedImages() {
    [...this.node.querySelectorAll('.dropzone-item')].forEach(element => {
      if (element.file) {
        this.dropzone.removeFile(element.file);
      }

      removeNode(element);
      this.dispatchUpdate('fileRemoved');
    });
  }

  removeCompletedFiles() {
    [...this.node.querySelectorAll('.dropzone-item.dz-complete')].forEach(element => {
      if (element.file) {
        this.dropzone.removeFile(element.file);
      }

      removeNode(element);
      this.dispatchUpdate('fileRemoved');
    });
  }

  updateFileList() {
    (this.dropzone.files || []).forEach(file => {
      // if file got remove from dom
      if (file && !file.previewElement.parentNode) {
        // remove it from dropzone
        this.dropzone.removeFile(file);
        this.dispatchUpdate('fileRemoved');
      }
    });
  }

  render(node, props) {
    const fileUploadId = `file-upload-${fileUploadCount++}`;
    node.setAttribute('id', fileUploadId);

    const previewTemplateNode = node.querySelector('.dropzone-item-template');
    removeClass(previewTemplateNode, 'dropzone-item-template');
    previewTemplateNode.style.display = '';

    const previewTemplate = outerHTML(previewTemplateNode);
    removeNode(previewTemplateNode);

    const dropzone = new Dropzone(node, {
      url: props.url,
      paramName: 'file',
      params: {
        _wp_http_referer: APP.REFERER,
      },
      maxFiles: props.maxFiles,
      parallelUploads: 2,
      maxFilesize: props.maxFilesize,
      acceptedFiles: props.acceptedFiles,
      previewTemplate: previewTemplate,
      previewsContainer: `#${fileUploadId} .dropzone-items`,
      clickable: `#${fileUploadId} .dropzone-select`,
      accept: (file, done) => {
        this.updateFileList();

        if (props.maxFilesize > 0 && props.maxFilesizeCombined) {
          const totalFilesize = [...node.querySelectorAll('.dropzone-item')]
            .filter(dropzoneItem => !hasClass(dropzoneItem, 'dz-error'))
            .reduce((size, dropzoneItem) => size + dropzoneItem.file.size, 0);

          const maxFilesizeInBytes = props.maxFilesize * 1000 * 1000;

          if (totalFilesize > maxFilesizeInBytes) {
            file.status = Dropzone.CANCELED;
            dropzone._errorProcessing([file], 'File is too big', null);
            return;
          }
        }

        done();
      },
    });

    this.dropzone = dropzone;

    dropzone.on('sending', function (file, xhr, formData) {
      formData.append('_wpnonce', APP.WP_REST_NONCE);
      formData.append('nonce', APP.NONCE);
    });

    dropzone.on('addedfile', file => {
      if (props.maxFiles === 1) {
        this.removeCompletedFiles();
      }

      file.previewElement.file = file;
      this.dispatchUpdate('fileAdded');
    });

    dropzone.on('complete', file => {
      if (file.xhr && file.xhr.responseText) {
        updateNonce(file.xhr.responseText);
      }

      file.previewElement.file = file;

      setFileUploadStatus(file, file.status);
      this.dispatchUpdate('fileUploadComplete');
    });

    dropzone.on('error', (file, response) => {
      this.updateFileList();

      const responseText = response
        ? typeof response === 'string'
          ? response
          : response.message
        : '';

      file.previewElement.file = file;

      const errorMessageNode = file.previewElement.querySelector('.dropzone-error');
      const fallbackMessage = errorMessageNode.getAttribute('data-fallback-errormessage');
      const maxFilesizeMessage = errorMessageNode.getAttribute('data-maxfilesize-errormessage');
      const maxFilesizeReached = responseText.indexOf('File is too big') !== -1;
      const errorMessageText = maxFilesizeReached ? maxFilesizeMessage : fallbackMessage;
      errorMessageNode.innerHTML =
        response && response.message ? response.message : errorMessageText;
      this.dispatchUpdate('fileUploadError');
    });

    dropzone.on('maxfilesreached', () => {
      this.updateFileList();

      // NOTE: dirty hack: wait a bit for other errors messages to be processed
      // and then override error message text with maxfiles reached message in all items that failed
      setTimeout(() => {
        [...node.querySelectorAll('.dropzone-item.dz-error')].forEach(dropzoneItem => {
          const errorMessageNode = dropzoneItem.querySelector('.dropzone-error');
          errorMessageNode.innerHTML = errorMessageNode.getAttribute('data-maxfiles-errormessage');
        });
      }, 500);
    });

    dropzone.on('success', (file, response) => {
      file.previewElement.file = file;

      if (!(response && response.data && response.data.name && response.data.upload)) {
        const errorMessageNode = file.previewElement.querySelector('.dropzone-error');
        errorMessageNode.innerHTML = errorMessageNode.getAttribute('data-fallback-errormessage');

        setFileUploadStatus(file, 'error');
        return;
      }

      file.previewElement.querySelector('.file-upload-value').value = JSON.stringify(response.data);

      this.dispatchUpdate('fileUploadSuccess');

      setFileUploadStatus(file, 'success');
    });

    dropzone.on('removedfile', () => {
      this.dispatchUpdate('fileRemoved');
    });

    node.querySelector('.dropzone-select').addEventListener('click', preventDefault);
    [...node.querySelectorAll('.dropzone-item-uploaded .dropzone-delete')].forEach(element => {
      element.addEventListener('click', this.removeDropzoneItem.bind(this));
    });
  }

  removeDropzoneItem(event) {
    removeNode(closest(event.currentTarget, '.dropzone-item-uploaded'));
    this.dispatchUpdate('fileRemoved');
  }

  dispatchUpdate(updateEvent) {
    this.node.dispatchEvent(createEvent(updateEvent));
    this.node.dispatchEvent(createEvent('fileListUpdated'));
  }
}

function preventDefault(event) {
  event.preventDefault();
}

function setFileUploadStatus(file, status) {
  const progressBar = file.previewElement.querySelector('.progress-bar');

  switch (status) {
    case 'success':
      removeClass(progressBar, 'bg-primary');
      addClass(progressBar, 'bg-success');
      addClass(file.previewElement, 'bg-light-success');
      break;
    case 'error':
    default:
      removeClass(progressBar, 'bg-primary');
      addClass(progressBar, 'bg-danger');
      addClass(file.previewElement, 'bg-light-danger');

      // NOTE: remove hidden inputs so they don't get submitted (upload failed anyway)
      removeNode(file.previewElement.querySelector('.file-upload-value'));
  }
}
